/*
 * Esse Werks — gap engine (shared between homepage Path Finder and kit PDP).
 *
 * Per the MODEL-RESET (claude-code-prompts/_status/MODEL-RESET.md):
 *
 *   Kit    = standing pre-defined product. Priced at Σ × 0.92 via the
 *            product's own variant.price (set by scripts/phase1-1a-kit-pricing).
 *   Bundle = dynamic configurator output (kit minus owned parts + tune).
 *            Priced at Σ × 0.95. NOT a Shopify product; built per cart.
 *
 * Two surfaces consume this module:
 *   - Tune Builder (assets/ew-tune-builder.js)
 *   - Kit PDP "Own some of these parts?" surface (this file's compute()
 *     called inline from sections/main-product-bundle.liquid)
 *
 * The math lives ONCE — both surfaces import the same constants and
 * the same compute function. If the discount math ever moves again,
 * it moves here, and both surfaces stay in sync.
 */
(function () {
  'use strict';

  /* Per MODEL-RESET — neither percentage is rendered in UI. */
  var BUNDLE_DISCOUNT = 0.05;  /* configurator output: Σ × 0.95 */
  var KIT_DISCOUNT    = 0.08;  /* standing kit:        Σ × 0.92 (kept for completeness) */

  function round2(n) { return Math.round(n * 100) / 100; }

  /**
   * Compute the configurator bundle from a kit recipe + the customer's
   * owned-component selection.
   *
   *   input: {
   *     components: [{ handle, title, variantId, price, quantity }, ...]
   *       — the kit's full recipe (from product.bundle_components or the
   *         path-finder's gap-core table).
   *     tune:       { handle, title, variantId, price } | null
   *       — the matched tune for this kit's engine/chassis. Always
   *         included in the resulting bundle; doesn't count toward the
   *         minimum-2 rule.
   *     ownedHandles: [handle, ...]
   *       — components the customer has already toggled "I have this".
   *   }
   *
   *   output: {
   *     remaining:        [component, ...]   — components NOT owned
   *     tune:             tune | null         — always included
   *     items:            [variantId, ...]    — flat list for /cart/add
   *     priceSubtotal:    number              — Σ of remaining + tune
   *     priceBundle:      number              — priceSubtotal × 0.95
   *     priceSavings:     number              — priceSubtotal − priceBundle
   *     valid:            boolean             — true if remaining.length ≥ 2
   *     reason:           'ok' | 'too-few-parts'
   *   }
   */
  function compute(input) {
    var components = (input && input.components) || [];
    var tune       = (input && input.tune) || null;
    var owned      = ((input && input.ownedHandles) || []).reduce(function (acc, h) {
      acc[h] = true; return acc;
    }, {});

    var remaining = components.filter(function (c) { return !owned[c.handle]; });

    var subtotal = 0;
    remaining.forEach(function (c) { subtotal += (c.price || 0) * (c.quantity || 1); });
    if (tune) subtotal += (tune.price || 0);

    var bundle  = round2(subtotal * (1 - BUNDLE_DISCOUNT));
    var savings = round2(subtotal - bundle);

    var items = [];
    remaining.forEach(function (c) {
      if (c.variantId) items.push({ id: c.variantId, quantity: c.quantity || 1 });
    });
    if (tune && tune.variantId) items.push({ id: tune.variantId, quantity: 1 });

    var valid  = remaining.length >= 2;
    var reason = valid ? 'ok' : 'too-few-parts';

    return {
      remaining: remaining, tune: tune, items: items,
      priceSubtotal: subtotal, priceBundle: bundle, priceSavings: savings,
      valid: valid, reason: reason
    };
  }

  /**
   * Add the resulting bundle to the cart, then navigate to /cart.
   * Strategy (per MODEL-RESET): POST /cart/add.js with the items + the
   * `_via=kit-pdp-configurator` and `_bundle_id` line properties. A
   * server-side AUTOMATIC Discount function keys on `_via` and applies the
   * 5% (Σ×0.95) to the marked lines — no /discount URL needed. The `_via`
   * marker is therefore load-bearing and must stay on every added line.
   *
   * Returns a Promise that resolves when navigation begins.
   */
  function addBundleToCart(items, options) {
    options = options || {};
    var bundleId = options.bundleId || ('ew-bundle-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7));
    var props = {
      '_via': options.via || 'kit-pdp-configurator',
      '_bundle_id': bundleId
    };
    if (options.vehicleLabel) props['Vehicle'] = options.vehicleLabel;

    var payload = {
      items: items.map(function (it) {
        return { id: it.id, quantity: it.quantity || 1, properties: props };
      })
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed: ' + r.status);
      return r.json();
    }).then(function () {
      window.location.href = '/cart';
    });
  }

  window.ewGapEngine = {
    compute: compute,
    addBundleToCart: addBundleToCart,
    BUNDLE_DISCOUNT: BUNDLE_DISCOUNT,
    KIT_DISCOUNT: KIT_DISCOUNT
  };
})();
